import { createModule } from "saga-slice";
import { call, put } from "redux-saga/effects";
import { commercialService } from "../services/commercialService";

const initialState = {
    list: [],
    loading: false,
    error: null,

    // --- Performance par période (batch) ---
    performanceById: {},     // { [commercialId]: { plan, baseUsed, baseTotal, objective, totalCommission, ... } }
    perfPeriod: "DAYS_30",
    perfRefDate: new Date().toISOString().slice(0, 10),
    perfLoading: false,

    // --- Plans de commission (pour le select dans la modale Commercial) ---
    plans: [],
    plansLoading: false,

    ledgerByCommercialId: {}, // { [commercialId]: Array<LedgerRow> }
    ledgerLoading: false,

    exportLoading: false,
};

const commercialSlice = createModule({

    name: 'commercials',
    initialState,
    reducers: {
        list: (state) => {
            state.loading = true;
        },
        create: (state) => {
            state.loading = true;
        },
        update: (state) => {
            state.loading = true;
        },
        delete: (state) => {
            state.loading = true;
        },
        previewCommission: (state) => {
            state.loading = true;
        },
        postCommission: (state) => {
            state.loading = true;
        },

        // ---- batch performance ----
        loadPerformance: (state, { payload }) => {
            state.perfLoading = true;
            if (payload?.period) state.perfPeriod = payload.period;
            if (payload?.refDate) state.perfRefDate = payload.refDate;
            if (!payload?.refDate && payload?.endDate) state.perfRefDate = payload.endDate;
        },
        fetchedPerformance: (state, payload) => {
            const map = {};
            (payload || []).forEach(p => { map[p.commercialId] = p; });
            console.log("Mapped performance data:", payload);
            state.performanceById = map;
            state.perfLoading = false;
        },
        clearPerformance: (state) => {
            state.performanceById = {};
            state.perfLoading = false;
        },

        // ---- Ledger reducers ----
        listLedger: (state) => { state.ledgerLoading = true; },
        fetchedLedger: (state, payload) => {
            const { commercialId, rows } = payload || {};
            if (commercialId != null) state.ledgerByCommercialId[commercialId] = rows || [];
            state.ledgerLoading = false;
        },
        reverseCommissionLedger: (state) => { state.ledgerLoading = true; },
        listLedgerEffective: (state) => { state.ledgerLoading = true; },

        exportLedger: (state) => { state.exportLoading = true; },
        exportLedgerDone: (state) => { state.exportLoading = false; },


        fetchedData: (state, payload) => {
            state.loading = false;
            state.list = payload;
        },

        finishFetching: (state) => {
            state.loading = false;
        },

        fetchError: (state, action) => {
            state.loading = false;
            state.error = action || "An error occurred";
        },
    },
    sagas: (A) => ({
        *[A.list]({ payload }) {
            try {
                const { data } = yield call(commercialService.list, payload);
                yield put(A.fetchedData(data));
            } catch (error) {
                yield put(A.finishFetching());
                yield put(A.fetchError());
            }
        },

        *[A.create]({ payload }) {
            try {
                const { data } = yield call(commercialService.create, payload.data);
                yield put(A.finishFetching());
                yield call(payload.onSuccess, data);
            } catch (error) {
                yield put(A.finishFetching());
                yield put(A.fetchError());
            }
        },

        *[A.update]({ payload }) {
            try {
                const { data } = yield call(commercialService.update, payload.data);
                yield put(A.finishFetching());
                yield call(payload.onSuccess, data);
            } catch (error) {
                yield put(A.finishFetching());
                yield put(A.fetchError());
            }
        },
        *[A.delete]({ payload }) {
            try {
                yield call(commercialService.delete, payload);

                yield put(A.finishFetching());
                yield put(A.list());
            } catch (error) {
                yield put(A.finishFetching());
                yield put(A.fetchError());
            }
        },
        *[A.previewCommission]({ payload }) {
            const { commercialId, period, refDate, startDate, endDate, onSuccess, onError } = payload;
            try {
                const { data } = yield call(commercialService.previewCommission, { commercialId, period, refDate, startDate, endDate });
                yield put(A.finishFetching());
                if (onSuccess) yield call(onSuccess, data);
                yield put(commercialActions.list({})); // si tu veux rafraîchir ailleurs
            } catch (error) {
                yield put(A.finishFetching());
                yield put(A.fetchError());
                if (onError) {
                    yield call(onError, error);
                }
            }
        },
        *[A.postCommission]({ payload }) {
            const { commercialId, period, refDate, ref, startDate, endDate, onSuccess, onError } = payload;
            try {
                const { data } = yield call(commercialService.postCommission, { commercialId, period, refDate, ref, startDate, endDate });
                yield put(A.finishFetching());
                if (onSuccess) yield call(onSuccess, data);
                yield put(commercialActions.list({})); // si tu veux rafraîchir ailleurs
            } catch (error) {
                yield put(A.finishFetching());
                yield put(A.fetchError());
                if (onError) {
                    yield call(onError, error);
                }
            }
        },

        // ---------- PERF (batch) ----------
        *[A.loadPerformance]({ payload }) {
            try {
                const startDate = payload?.startDate;
                const endDate = payload?.endDate;
                const baseRate = payload?.baseRate;
                const { data } = yield call(commercialService.loadPerformance, { startDate, endDate, baseRate });
                console.log("Loaded performance data:", data);
                yield put(A.fetchedPerformance(data));
            } catch (error) {
                yield put(A.fetchError(error?.message));
                yield put(A.clearPerformance());
            }
        },

        *[A.listLedger]({ payload }) {
            try {
                const { commercialId, start, end, onSuccess } = payload;
                const { data } = yield call(commercialService.listLedger, { commercialId, start, end });
                yield put(A.fetchedLedger({ commercialId: commercialId || 0, rows: data }));
                if (onSuccess) yield call(onSuccess, data);
            } catch (error) {
                yield put(A.fetchError(error?.message));
            }
        },

        *[A.listLedgerEffective]({ payload }) {
            try {
                const { commercialId, start, end, onSuccess } = payload;
                const { data } = yield call(commercialService.listLedgerEffective, { commercialId, start, end });
                yield put(A.fetchedLedger({ commercialId: commercialId || 0, rows: data }));
                if (onSuccess) yield call(onSuccess, data);
            } catch (error) {
                yield put(A.fetchError(error?.message));
            }
        },

        *[A.reverseCommissionLedger]({ payload }) {
            try {
                const { commercialId, period, refDate, ref = "ui-reverse", onSuccess, onError } = payload;
                yield call(commercialService.reverseCommission, { commercialId, period, refDate, ref });
                // Reload ledger after reversal
                yield put(A.listLedger({ commercialId }));
                if (onSuccess) yield call(onSuccess);
            } catch (error) {
                yield put(A.fetchError(error?.response?.data?.message || error.message));
                if (payload?.onError) yield call(payload.onError, error);
            } finally {
                yield put(A.finishFetching());
            }
        },

        *[A.exportLedger]({ payload }) {
            const { commercialId, start, end, period, status, filename, onSuccess, onError } = payload || {};
            try {
                const { data, headers } = yield call(commercialService.exportLedger, { commercialId, start, end, period, status });

                // Try to infer filename from headers, else use provided, else fallback
                const headerName = headers?.["content-disposition"];
                let suggested = filename || "commission_ledger.csv";
                if (headerName) {
                    const match = /filename\*=UTF-8''([^;]+)/i.exec(headerName) || /filename="?([^"]+)"?/i.exec(headerName);
                    if (match && match[1]) suggested = decodeURIComponent(match[1]);
                }

                // Trigger download in browser
                const blob = new Blob([data], { type: "text/csv;charset=utf-8" });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = suggested;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

                if (onSuccess) yield call(onSuccess);
            } catch (err) {
                if (onError) yield call(onError, err);
            } finally {
                yield put(A.exportLedgerDone());
            }
        },
    }),
});

export default commercialSlice;
export const commercialActions = commercialSlice.actions;


